// @flow
import { useCallback, useState } from "react";
import { css } from "styled-components";
import gql from "graphql-tag";
import { debounce } from "lodash";
import { useQuery } from "@apollo/client";
import { useCombobox } from "downshift";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faTimes } from "@fortawesome/free-solid-svg-icons";
import { ArrowContainer, Container, Input, Options, Option } from "./Select";
import { Label } from "./Label";

const GET_SEARCH_AREAS = gql`
  query ListSearchAreas($input: String!) {
    searchAreas(input: $input) {
      value: id
      label: name
      localAuthorityDistrict
    }
  }
`;

const clearButtonStyle = css`
  height: 100%;
  color: ${({ theme }) => theme.palette.hague};
  padding: 0 18px 0;
  opacity: 0.5;
  display: flex;
  align-items: center;
  z-index: 1;
  margin: 0;
  right: 0;
  position: absolute;
  top: 50%;
  border: none;
  background-color: transparent;
  transform: translateY(-50%);
  cursor: pointer;
`;

const localAuthorityDistrictStyle = css`
  font-size: 14px;
  color: ${({ theme }) => theme.palette.hague40};
`;

type SearchArea = {|
  label: string,
  value: string,
|};

type Props = {
  onChange: (area: SearchArea | null) => void,
  value: SearchArea | null,
};

export const SearchAreaSelect = ({ onChange, value: selectedItem }: Props) => {
  const [inputValue, setInputValue] = useState(selectedItem?.label || "");
  const {
    previousData,
    data = previousData,
    loading,
  } = useQuery(GET_SEARCH_AREAS, {
    variables: { input: inputValue.replace(" (borough)", "").toLowerCase() },
    skip: inputValue.length === 0,
  });

  const debouncedOnChange = useCallback(
    debounce((state) => {
      setInputValue(state.inputValue);
    }, 500),
    [],
  );

  const results = data?.searchAreas || [];

  const {
    selectItem,
    isOpen,
    getMenuProps,
    getComboboxProps,
    getItemProps,
    getLabelProps,
    getInputProps,
    highlightedIndex,
    getToggleButtonProps,
  } = useCombobox({
    initialSelectedItem: selectedItem,
    items: results,
    itemToString: (item) => item?.label || "",
    onInputValueChange: debouncedOnChange,
    onSelectedItemChange: (changes) => {
      if (changes.selectedItem) {
        setInputValue(changes.selectedItem.label);
        onChange(changes.selectedItem);
      } else {
        setInputValue("");
        onChange(null);
      }
    },
  });

  return (
    <Container>
      <Label {...getLabelProps()}>Location</Label>
      <ArrowContainer {...getComboboxProps()}>
        <Input
          {...getToggleButtonProps()}
          {...getInputProps()}
          placeholder="Search for a location"
        />
        {loading && (
          <div css={clearButtonStyle}>
            <FontAwesomeIcon icon={faSpinner} spin />
          </div>
        )}
        {!loading && selectedItem && (
          <button
            css={clearButtonStyle}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              selectItem(null);
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        )}
      </ArrowContainer>
      {isOpen && inputValue.length > 0 && (
        <Options {...getMenuProps()}>
          {results.length === 0 && !loading && <Option>No results</Option>}
          {results.map((item, index) => (
            <Option
              {...getItemProps({
                item,
                index,
                style: {
                  backgroundColor: highlightedIndex === index ? "#FAF7F4" : "",
                  fontWeight: selectedItem === item ? "500" : "400",
                  border: highlightedIndex === index ? "solid 1px #F2EBE3" : "",
                },
              })}
            >
              <div>{item.label}</div>
              {item.localAuthorityDistrict && (
                <div css={localAuthorityDistrictStyle}>
                  {item.localAuthorityDistrict}
                </div>
              )}
            </Option>
          ))}
        </Options>
      )}
    </Container>
  );
};
